import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  HiChartPie,
  HiUsers,
  HiOutlineDatabase,
  HiChevronDown,
  HiChevronRight,
  HiOutlineExternalLink,
  HiInbox,
  HiOutlineCog,
  HiPaperAirplane,
  HiOutlineMail,
  HiOutlineQuestionMarkCircle,
  HiOutlineCash,
  HiOutlineReceiptTax,
  HiOutlineShieldCheck,
  HiBan,
  HiSearch,
} from "react-icons/hi";
import { useAuth } from "../context/AuthContext";

const AdminSidebar = ({ handleShowType, activeMenu, isOpen, onClose }) => {
  const { getProfile } = useAuth();
  const [userInfo, setUserInfo] = useState({});
  const [expandedSections, setExpandedSections] = useState({
    email: false,
    finances: false,
    rateLimiting: false
  });
  const navigate = useNavigate();

  useEffect(() => {
    setUserInfo(getProfile());
  }, [getProfile]);

  const isExpanded = true;
  const sidebarWidth = "w-[84vw] max-w-72 lg:w-72";
  const adminCardHeight = "h-24";

  const adminName = userInfo?.name || "Admin User";
  const adminInitial = String(adminName || "A").trim().charAt(0).toUpperCase() || "A";
  
  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };
  
  return (
    <>
      {/* Mobile Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm transition-opacity duration-300"
          onClick={onClose}
        />
      )}

      <aside 
        className={`${sidebarWidth} bg-[#161616] shadow-sm z-50 border-r border-white/5 lg:z-10 overflow-hidden 
          fixed inset-y-0 left-0 transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'} 
          lg:static lg:translate-x-0 lg:shadow-none lg:flex-shrink-0`}
      >
        <div className="h-full flex flex-col overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
        {/* Admin Card */}
        <div className={`${adminCardHeight} transition-all duration-300 flex-shrink-0 flex items-center justify-center ${isExpanded ? 'p-4' : 'p-0'}`}>
          {isExpanded ? (
            <div className="flex flex-col justify-center h-full w-full">
              <div className="bg-[#BDE958] rounded-xl px-4 py-3 shadow-sm border border-black/5 h-full flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    {userInfo?.imageUrl ? (
                      <img
                        className="h-11 w-11 rounded-full object-cover border-2 border-[#D9E1CC]"
                        src={userInfo.imageUrl}
                        alt={adminName}
                      />
                    ) : (
                      <div className="h-11 w-11 rounded-full border-2 border-[#D9E1CC] bg-white/80 text-[#161616] flex items-center justify-center font-black">
                        {adminInitial}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[#161616] font-black text-sm truncate max-w-[150px]">
                      {adminName}
                    </span>
                    <span className="text-[#161616]/60 text-[11px] font-semibold tracking-wide">
                      {(() => {
                        if (userInfo?.accessLevel === 0) return "Super Admin";
                        if (userInfo?.accessLevel === 1) return "CRM Admin";
                        if (userInfo?.accessLevel === 2) return "API Manager";
                        if (userInfo?.accessLevel === 3) return "Support";
                        return "User";
                      })()}
                    </span>
                  </div>
                </div>
                <button 
                  className="p-1.5 rounded-full bg-white/20 hover:bg-white/40 text-[#161616] transition-colors"
                  onClick={() => navigate('/profile')}
                  title="Profile Settings"
                >
                  <HiOutlineCog className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            <div className="h-12 w-12 rounded-full overflow-hidden border-2 border-gray-700 shadow-lg">
              {userInfo?.imageUrl ? (
                <img
                  className="h-full w-full object-cover"
                  src={userInfo.imageUrl}
                  alt={adminName}
                />
              ) : (
                <div className="h-full w-full bg-white/80 text-gray-900 flex items-center justify-center font-extrabold">
                  {adminInitial}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Menu Items */}
        <nav className="mt-4 flex flex-col space-y-1 px-2 flex-grow overflow-y-auto">
          {/* Users - Now first item */}
          <button
            onClick={() => handleShowType(1)}
            className={`flex items-center px-3 py-2.5 rounded-full transition-colors ${
              activeMenu === 1
                ? "bg-[#BDE958] text-[#161616]"
                : "text-gray-300 hover:bg-[#232323]"
            }`}
          >
            <div className="mr-3"><HiUsers className="w-5 h-5" /></div>
            {isExpanded && <span className="text-sm font-medium">Users</span>}
          </button>

          {/* Analytics - Now second item */}
          <button
            onClick={() => handleShowType(7)}
            className={`flex items-center px-3 py-2.5 rounded-full transition-colors ${
              activeMenu === 7
                ? "bg-[#BDE958] text-[#161616]"
                : "text-gray-300 hover:bg-[#232323]"
            }`}
          >
            <div className="mr-3"><HiChartPie className="w-5 h-5" /></div>
            {isExpanded && <span className="text-sm font-medium">Analytics</span>}
          </button>
          
          {/* APIs */}
          <button
            onClick={() => handleShowType(2)}
            className={`flex items-center px-3 py-2.5 rounded-full transition-colors ${
              activeMenu === 2
                ? "bg-[#BDE958] text-[#161616]"
                : "text-gray-300 hover:bg-[#232323]"
            }`}
          >
            <div className="mr-3"><HiOutlineDatabase className="w-5 h-5" /></div>
            {isExpanded && <span className="text-sm font-medium">APIs</span>}
          </button>
          
          {/* Dashboard - commented out as per requirements */}
          {/* <button
            onClick={() => handleShowType(3)}
            className={`flex items-center px-3 py-3 rounded-lg transition-colors ${
              activeMenu === 3
                ? "bg-blue-900/20 text-blue-400"
                : "text-gray-300 hover:bg-[#161616]"
            }`}
          >
            <div className="mr-3"><HiOutlineChartBar className="w-5 h-5" /></div>
            {isExpanded && <span className="text-sm font-medium">Dashboard</span>}
          </button> */}
          
          {/* Email Section */}
          <div>
            <button
              onClick={() => toggleSection('email')}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-full transition-colors text-gray-300 hover:bg-[#232323]"
            >
              <div className="flex items-center">
                <div className="mr-3"><HiOutlineMail className="w-5 h-5" /></div>
                {isExpanded && <span className="text-sm font-medium">Email</span>}
              </div>
              {isExpanded && (
                <div className="text-gray-400">
                  {expandedSections.email ? (
                    <HiChevronDown className="w-4 h-4" />
                  ) : (
                    <HiChevronRight className="w-4 h-4" />
                  )}
                </div>
              )}
            </button>
            
            {isExpanded && expandedSections.email && (
              <div className="ml-8 mt-1 space-y-1">
                <button
                  onClick={() => handleShowType(4)}
                  className={`w-full flex items-center px-3 py-2 rounded-lg transition-colors ${
                    activeMenu === 4
                      ? "bg-[#BDE958]/10 text-[#BDE958]"
                      : "text-gray-300 hover:bg-[#232323]"
                  }`}
                >
                  <div className="mr-3"><HiInbox className="w-4 h-4" /></div>
                  <span className="text-sm">Subscription Inbox</span>
                </button>
                <button
                  onClick={() => handleShowType(5)}
                  className={`w-full flex items-center px-3 py-2 rounded-lg transition-colors ${
                    activeMenu === 5
                      ? "bg-[#BDE958]/10 text-[#BDE958]"
                      : "text-gray-300 hover:bg-[#232323]"
                  }`}
                >
                  <div className="mr-3"><HiOutlineQuestionMarkCircle className="w-4 h-4" /></div>
                  <span className="text-sm">Inquiry Inbox</span>
                </button>
                <button
                  onClick={() => handleShowType(6)}
                  className={`w-full flex items-center px-3 py-2 rounded-lg transition-colors ${
                    activeMenu === 6
                      ? "bg-[#BDE958]/10 text-[#BDE958]"
                      : "text-gray-300 hover:bg-[#232323]"
                  }`}
                >
                  <div className="mr-3"><HiPaperAirplane className="w-4 h-4" /></div>
                  <span className="text-sm">Compose</span>
                </button>
              </div>
            )}
          </div>


          {/* Rate Limiting Section */}
          <div>
            <button
              onClick={() => toggleSection('rateLimiting')}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-full transition-colors text-gray-300 hover:bg-[#232323]"
            >
              <div className="flex items-center">
                <div className="mr-3"><HiOutlineShieldCheck className="w-5 h-5" /></div>
                {isExpanded && <span className="text-sm font-medium">Rate limiting</span>}
              </div>
              {isExpanded && (
                <div className="text-gray-400">
                  {expandedSections.rateLimiting ? (
                    <HiChevronDown className="w-4 h-4" />
                  ) : (
                    <HiChevronRight className="w-4 h-4" />
                  )}
                </div>
              )}
            </button>
            
            {isExpanded && expandedSections.rateLimiting && (
              <div className="ml-8 mt-1 space-y-1">
                <button
                  onClick={() => handleShowType(10)}
                  className={`w-full flex items-center px-3 py-2 rounded-lg transition-colors ${
                    activeMenu === 10
                      ? "bg-[#BDE958]/10 text-[#BDE958]"
                      : "text-gray-300 hover:bg-[#232323]"
                  }`}
                >
                  <div className="mr-3"><HiBan className="w-4 h-4" /></div>
                  <span className="text-sm">Blocked IPs</span>
                </button>
                <button
                  onClick={() => handleShowType(11)}
                  className={`w-full flex items-center px-3 py-2 rounded-lg transition-colors ${
                    activeMenu === 11
                      ? "bg-[#BDE958]/10 text-[#BDE958]"
                      : "text-gray-300 hover:bg-[#232323]"
                  }`}
                >
                  <div className="mr-3"><HiSearch className="w-4 h-4" /></div>
                  <span className="text-sm">IP Lookups</span>
                </button>
                <button
                  onClick={() => handleShowType(13)}
                  className={`w-full flex items-center px-3 py-2 rounded-lg transition-colors ${
                    activeMenu === 13
                      ? "bg-[#BDE958]/10 text-[#BDE958]"
                      : "text-gray-300 hover:bg-[#232323]"
                  }`}
                >
                  <div className="mr-3"><HiOutlineCog className="w-4 h-4" /></div>
                  <span className="text-sm">Settings</span>
                </button>
              </div>
            )}
          </div>

          {/* Settings */}
          <button
            onClick={() => handleShowType(12)}
            className={`flex items-center px-3 py-2.5 rounded-full transition-colors ${
              activeMenu === 12
                ? "bg-[#BDE958] text-[#161616]"
                : "text-gray-300 hover:bg-[#232323]"
            }`}
          >
            <div className="mr-3"><HiOutlineCog className="w-5 h-5" /></div>
            {isExpanded && <span className="text-sm font-medium">Settings</span>}
          </button>

          {/* Finances Section */}
          <div>
            <button
              onClick={() => toggleSection('finances')}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-full transition-colors text-gray-300 hover:bg-[#232323]"
            >
              <div className="flex items-center">
                <div className="mr-3"><HiOutlineCash className="w-5 h-5" /></div>
                {isExpanded && <span className="text-sm font-medium">Finances</span>}
              </div>
              {isExpanded && (
                <div className="text-gray-400">
                  {expandedSections.finances ? (
                    <HiChevronDown className="w-4 h-4" />
                  ) : (
                    <HiChevronRight className="w-4 h-4" />
                  )}
                </div>
              )}
            </button>
            
            {isExpanded && expandedSections.finances && (
              <div className="ml-8 mt-1 space-y-1">
                <button
                  onClick={() => handleShowType(8)}
                  className={`w-full flex items-center px-3 py-2 rounded-lg transition-colors ${
                    activeMenu === 8
                      ? "bg-[#BDE958]/10 text-[#BDE958]"
                      : "text-gray-300 hover:bg-[#232323]"
                  }`}
                >
                  <div className="mr-3"><HiOutlineCash className="w-4 h-4" /></div>
                  <span className="text-sm">Transactions</span>
                </button>
                <button
                  onClick={() => handleShowType(9)}
                  className={`w-full flex items-center px-3 py-2 rounded-lg transition-colors group ${
                    activeMenu === 9
                      ? "bg-[#BDE958]/10 text-[#BDE958]"
                      : "text-gray-300 hover:bg-[#232323]"
                  }`}
                >
                  <div className="mr-3"><HiOutlineReceiptTax className="w-4 h-4" /></div>
                  <span className="text-sm">Invoices</span>
                  <HiOutlineExternalLink className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              </div>
            )}
          </div>
        </nav>
      </div>
    </aside>
    </>
  );
};

export default AdminSidebar;
